import type { ReactNode } from "react";

/**
 * The few glyphs the Chats screens draw that the Figma exports do not have, or that must take the
 * color of the text beside them (a white plus on a graphite button; an amber clock). Drawn at the
 * weight of the exported set: a 1.5 stroke on a 24 grid, round ends.
 */
export function Glyph({
  size = 14,
  strokeWidth = 1.6,
  children,
}: {
  size?: number | undefined;
  strokeWidth?: number | undefined;
  children: ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      {children}
    </svg>
  );
}

export type IconProps = { size?: number | undefined };

export const PlusIcon = ({ size }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="M12 5v14M5 12h14" />
  </Glyph>
);

export const PersonIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size}>
    <circle cx="12" cy="8.5" r="4" />
    <path d="M4.5 20.5c1-3.9 4-6 7.5-6s6.5 2.1 7.5 6" />
  </Glyph>
);

export const TrashIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size}>
    <path d="M4 7h16M9.5 7V5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
  </Glyph>
);

export const PencilIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size}>
    <path d="M14.5 5.5l4 4M4 20l1-4.5L15.8 4.7a1.4 1.4 0 0 1 2 0l1.5 1.5a1.4 1.4 0 0 1 0 2L8.5 19 4 20z" />
  </Glyph>
);

export const SearchIcon = ({ size }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Glyph>
);

export const CloseIcon = ({ size }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Glyph>
);

export const ChevronDownIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={2.2}>
    <path d="m6 9 6 6 6-6" />
  </Glyph>
);

export const ChevronRightIcon = ({ size = 11 }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="m9 6 6 6-6 6" />
  </Glyph>
);

export const PaperclipIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <path d="M21 11.5 12.5 20a5.5 5.5 0 0 1-8-8l9-9a3.7 3.7 0 0 1 5.2 5.2l-8.5 8.5a1.8 1.8 0 0 1-2.6-2.6L15 7.5" />
  </Glyph>
);

export const FileIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <path d="M14 2H6a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8Z" />
    <path d="M14 2v6h5" />
  </Glyph>
);

export const MessageIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H4l-2 2v-9.5A8.5 8.5 0 1 1 21 11.5Z" />
  </Glyph>
);

/** A speech bubble in outline: what Abacor said about its work, as opposed to what it did. */
export const BubbleIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <path d="M20 4H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h4v4l5-4h7a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1Z" />
  </Glyph>
);

export const StepsIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="18" cy="18" r="2.5" />
    <path d="M8.5 6H14a3 3 0 0 1 0 6h-4a3 3 0 0 0 0 6h5.5" />
  </Glyph>
);

export const BranchIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <circle cx="6" cy="5.5" r="2.5" />
    <circle cx="6" cy="18.5" r="2.5" />
    <circle cx="18" cy="9" r="2.5" />
    <path d="M6 8v8M18 11.5c0 4-6 3-11 5" />
  </Glyph>
);

export const ArchiveIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <rect x="3" y="4" width="18" height="4.5" rx="1.5" />
    <path d="M5 8.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5M9.5 12.5h5" />
  </Glyph>
);

export const ClockIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5M12 7v5l3 2" />
  </Glyph>
);

/** A screen with a dot on it: showing Abacor something on screen. */
export const ScreenIcon = ({ size = 15 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <rect x="3" y="4" width="18" height="13" rx="2.5" />
    <path d="M9 21h6M12 17v4" />
    <circle cx="12" cy="10.5" r="2" fill="currentColor" stroke="none" />
  </Glyph>
);

export const PanelIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M14.5 4v16" />
  </Glyph>
);

export const MoreIcon = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5.5" cy="12" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="18.5" cy="12" r="1.6" />
  </svg>
);

export const AtIcon = ({ size = 15 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <circle cx="12" cy="12" r="3.5" />
    <path d="M15.5 8.5v4.2a2.3 2.3 0 0 0 4.5 0V12a8 8 0 1 0-3.2 6.4" />
  </Glyph>
);

/** A stop: a small rounded square, filled, which is what a run being stopped looks like everywhere. */
/**
 * A square on a round button: 8px on the 24px circle and 12px on the 36px one a phone has, about a
 * third of it as the Paper board draws it. A `size-` class is what exempts it from the button's own
 * rule that sizes every icon to 16px, which made it two thirds of the circle. (A percentage does
 * not work: the button sizes its track to the icon, so a share of it is a share of nothing.)
 */
export const StopIcon = () => (
  <svg viewBox="0 0 10 10" fill="currentColor" aria-hidden="true" className="size-2 max-md:size-3">
    <rect width="10" height="10" rx="2.25" />
  </svg>
);

export const CheckIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={2.4}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Glyph>
);

export const ArrowUpIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="M12 19V5M5.5 11.5 12 5l6.5 6.5" />
  </Glyph>
);

export const ArrowDownIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="M12 5v14M5.5 12.5 12 19l6.5-6.5" />
  </Glyph>
);

export const ArrowRightIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Glyph>
);

export const ArrowUpRightIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="M7 17 17 7M8 7h9v9" />
  </Glyph>
);

export const ArrowLeftIcon = ({ size = 16 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.5}>
    <path d="M10 4 3 12l7 8" />
  </Glyph>
);

export const ImageIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="m4 18 5-5 4 4 2.5-2.5L20 18" />
  </Glyph>
);

export const AlertIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.9}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.5M12 16.4v.2" />
  </Glyph>
);

export const RetryIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.9}>
    <path d="M20 12a8 8 0 1 1-2.6-5.9M20 4v4.5h-4.5" />
  </Glyph>
);

/** A curved arrow back: Undo. */
export const UndoIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
  </Glyph>
);

/** A curved arrow on: Redo. */
export const RedoIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
  </Glyph>
);

export const MailIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <rect x="3" y="5" width="18" height="14" rx="2.6" />
    <path d="m4 7.5 8 5.8 8-5.8" />
  </Glyph>
);

export const LockIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.9}>
    <rect x="5" y="11" width="14" height="9.5" rx="2.4" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </Glyph>
);

export const ChevronLeftIcon = ({ size = 16 }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="m15 6-6 6 6 6" />
  </Glyph>
);

export const ChevronRightLargeIcon = ({ size = 16 }: IconProps) => (
  <Glyph size={size} strokeWidth={2}>
    <path d="m9 6 6 6-6 6" />
  </Glyph>
);

export const ZoomIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.4-4.4M8.5 11h5M11 8.5v5" />
  </Glyph>
);

/** Opening a file larger, from a card or a row. */
export const ExpandIcon = ({ size = 12 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.9}>
    <path d="M4 9V4h5M20 15v5h-5M4 4l6 6M20 20l-6-6" />
  </Glyph>
);

export const DownloadIcon = ({ size = 13 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.8}>
    <path d="M12 4v11M7.5 11 12 15.5 16.5 11M5 20h14" />
  </Glyph>
);

export const UploadIcon = ({ size = 16 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M12 17v-6M9.5 13.5 12 11l2.5 2.5" />
  </Glyph>
);

export const CalendarIcon = ({ size = 14 }: IconProps) => (
  <Glyph size={size} strokeWidth={1.6}>
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </Glyph>
);

/** The mark Abacor speaks with, from the pair logo (the same two shapes as the workspace mark). */
export function AbacorMark({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="8 8 48 48"
      fill="currentColor"
      className="shrink-0 text-foreground"
      aria-hidden="true"
    >
      <path d="M8 40V24C8 15.16 15.16 8 24 8H36V20H24C21.79 20 20 21.79 20 24V40Z" />
      <path d="M56 24V40C56 48.84 48.84 56 40 56H28V44H40C42.21 44 44 42.21 44 40V24Z" />
    </svg>
  );
}
